<template>
    <form class="col-md-8 mx-auto">
        <div class="loader" v-if="loading"></div>
        <div v-if="!loading">
            <div class="btn-group" role="group" aria-label="Basic example">
                <button type="button"
                        class="btn btn-primary"
                        @click="backToRegister">
                    <i class="fas fa-th-list"></i> {{ $t('backToList') }}
                </button>
            </div>
            <page-title :title="$t('registers.mentors.title')"></page-title>
            <div class="card mt-3">
                <div class="card-header bg-primary text-white">
                    <div class="card-title h5 m-0">{{ $t('personalData') }}</div>
                </div>
                <div class="card-body">
                    <person :person="mentor.person"
                            :show-extended="true"
                            :requiredIdentification="false"></person>
                    <div class="row">
                        <div class="form-group col-md-12 col-lg-6 col-xl-4">
                            <label class="font-weight-bold" :for="'directorate' + _uid">{{
                                $t('registers.mentors.gridHeaders.directorate') }}</label>
                            <nomenclature-dropdown :load-by-parent-sort="true"
                                                   :parent-sort="nomenclature.Directorate"
                                                   v-model="mentor.directorateId"
                                                   :fieldName="$t('registers.mentors.gridHeaders.directorate')"></nomenclature-dropdown>
                        </div>
                        <div class="form-group col-md-12 col-lg-6 col-xl-4">
                            <label class="font-weight-bold" :for="'title' + _uid">{{
                                $t('registers.mentors.gridHeaders.id')
                                }}</label>
                            <input type="text" class="form-control" :id="'id' + _uid"
                                   v-model="mentor.id" readonly>
                        </div>
                    </div>
                </div>
            </div>
            <div class="card mt-3">
                <div class="card-header bg-primary text-white">
                    <div class="card-title h5 m-0">{{ $t('registers.mentors.gridHeaders.reasonForJoiningLikeMentor') }}
                    </div>
                </div>
                <div class="card-body row">
                    <div class="form-group col-lg-12">
                        <label class="font-weight-bold" :for="'managementBoardDecision' + _uid">{{
                            $t('registers.mentors.gridHeaders.decision') }}</label>
                        <input type="text" class="form-control" :id="'managementBoardDecision' + _uid"
                               v-model="mentor.decisionForJoiningMentor">
                    </div>
                    <div class="form-group col-md-12 col-lg-6 col-xl-4">
                        <label class="font-weight-bold" :for="'title' + _uid">{{
                            $t('registers.mentors.gridHeaders.startDate')}}</label>
                        <datepicker v-model="mentor.startDateMentoring"></datepicker>
                    </div>
                    <div class="form-group col-md-12 col-lg-6 col-xl-4">
                        <label class="font-weight-bold" :for="'title' + _uid">{{
                            $t('registers.mentors.gridHeaders.endDate')}}</label>
                        <datepicker v-model="mentor.endDateMentoring"></datepicker>
                    </div>
                    <div class="form-group col-12">
                        <label class="font-weight-bold" :for="'additionalInfo' + _uid">{{
                            $t('registers.mentors.gridHeaders.additionalInfo') }}</label>
                        <input type="text" class="form-control" :id="'additionalInfo' + _uid"
                               v-model="mentor.additionalInfoMentoring">
                    </div>
                </div>
            </div>
            <div class="card mt-3">
                <div class="card-header bg-primary text-white">
                    <div class="card-title h5 m-0">{{ $t('registers.participants.title') }}</div>
                </div>
                <div class="card-body">
                    <div v-for="(participant,index) in mentor.participantsDetails" :key="index">
                        <label class="font-weight-bold" :for="'title' + _uid">{{
                            $t('registers.participants.gridHeaders.participant')}}</label>
                        <div class="k-display-inline-flex">
                            <person-details :person="participant"></person-details>
                            <i class="fas fa-trash-alt k-align-self-center btn-danger pointer position-relative"
                               @click="removeParticipantDetails(participant,index)"></i>
                        </div>
                        <hr>
                    </div>
                    <div v-for="(participant,index) in mentor.participants" :key="index">
                        <label class="font-weight-bold" :for="'title' + _uid">{{
                            $t('registers.participants.gridHeaders.participant')}}</label>
                        <div class="k-display-inline-flex">
                            <person :personSearchType="personTypeSearch" :person="participant"
                                    :allow-input="false"
                                    :requiredIdentification="false"></person>
                            <i class="fas fa-trash-alt k-align-self-center btn-danger pointer position-relative"
                               @click="removeParticipant(index)"></i>
                        </div>
                        <hr>
                    </div>
                    <div v-if="(mentor.participants.length + mentor.participantsDetails.length) < 2">
                        <button type="button" class="btn btn-secondary" @click="addParticipant">{{
                            $t('registers.mentors.gridHeaders.addParticipant') }}
                        </button>
                    </div>
                </div>
            </div>

            <AuxiliaryRegister ref="auxiliaryRegisterRef"
                               :register-id="AuxiliaryRegisters.Mentors"
                               register-name="Mentor"
                               :entry-id="mentor.auxRegisterEntityId"></AuxiliaryRegister>

            <div class="text-center mt-3">
                <button type="button" class="btn btn-primary btn" @click="onSubmit">
                    <i class="fas fa-save"></i>
                    {{ $t('buttons.save.btnText') }}
                </button>
            </div>
        </div>
    </form>
</template>

<script>
    import PersonComponent from '../../../components/person/Person';
    import { Mentor, InstitutionDetails, Person } from '@/models/models'
    import DatePicker from '../../../components/datepicker/DatePicker';
    import NomenclatureDropdown from '../../../components/dropdown/NomenclatureDropdown';
    import { NomenclatureParentBySort, PersonSearchType, AuxiliaryRegisters } from '@/models/enums';
    import PersonDetails from "../../../components/person/PersonDetails";
    import PageTitle from '../../../components/common/PageTitle';
    import AuxiliaryRegister from '@/components/dynamic/AuxiliaryRegister';

    export default {
        components: {
            PersonDetails,
            NomenclatureDropdown,
            'person': PersonComponent,
            'datepicker': DatePicker,
            'person-details': PersonDetails,
            PageTitle,
            AuxiliaryRegister
        },
        data() {
            return {
                nomenclature: NomenclatureParentBySort,
                id: '',
                mentor: new Mentor(),
                institution: new InstitutionDetails(),
                personTypeSearch: PersonSearchType.Participant,
                loading: false,
                AuxiliaryRegisters: AuxiliaryRegisters
            }
        },
        methods: {
            onSubmit() {
                this.$validator.validateAll().then(result => {
                    if (result) {
                        this.mentor.auxRegisterModel = this.$refs.auxiliaryRegisterRef.getDynamicRegisterModelForSubmit();
                        if (this.id) {
                            this.edit();
                        } else {
                            this.create();
                        }
                    }
                });
            },
            addParticipant() {
                this.mentor.participants.push(new Person());
            },
            removeParticipant(index) {
                this.mentor.participants.splice(index, 1);
            },
            removeParticipantDetails(person, index) {
                this.mentor.participantsDetails.splice(index, 1);
                this.mentor.deletedParticipants.push(person.id);
            },
            backToRegister() {
                this.$router.push('/registers/mentor');
            },
            edit() {
                this.loading = true;
                this.$http.put('/api/mentor', this.mentor).then(result => {
                    this.loading = false;
                    this.$vs.notify({
                        title: this.$t('notifications.success'),
                        text: this.$t('notifications.successfulSavedData'),
                        position: 'top-center',
                        color: 'success'
                    });
                    this.backToRegister();
                }).catch(error => {
                    this.loading = false;
                    this.$vs.notify({
                        title: this.$t('request.response.status.' + this.$helpers.errorStatusCode(error)),
                        text: this.$helpers.errorText(error),
                        color: 'danger',
                        time: this.$store.getters.notificationDuration
                    });
                });
            },
            create() {
                this.loading = true;
                this.$http.post('/api/mentor', this.mentor).then(result => {
                    this.loading = false;
                    this.$vs.notify({
                        title: this.$t('notifications.success'),
                        text: this.$t('notifications.successfulSavedData'),
                        position: 'top-center',
                        color: 'success'
                    });
                    this.backToRegister();
                }).catch(error => {
                    this.loading = false;
                    this.$vs.notify({
                        title: this.$t('request.response.status.' + this.$helpers.errorStatusCode(error)),
                        text: this.$helpers.errorText(error),
                        color: 'danger',
                        time: this.$store.getters.notificationDuration
                    });
                });
            },
            loadData() {
                this.loading = true;
                this.$http.get(`/api/mentor/${this.id}`).then(result => {
                    this.mentor = new Mentor(result.data);
                    this.loading = false;
                }).catch(error => {
                    this.loading = false;
                    // TODO:
                });
            }
        },
        watch: {
            'mentor.institutionId'(newVal, oldVal) {
                if (this.mentor.institutionId !== undefined
                    && this.mentor.institutionId !== '' &&
                    this.mentor.institutionId != null) {
                    this.$http.get(`/api/Institution/GetDetails/${this.mentor.institutionId}`).then(result => {
                        this.institution = new InstitutionDetails(result.data);
                    });
                }
                if (newVal === 0) {
                    this.mentor.institutionId = '';
                }
            },
            'mentor.startDateMentoring'(newVal, oldVal) {
                if (newVal !== '' && newVal != null && newVal !== undefined) {
                    let year = new Date(newVal).getFullYear();
                    let date = new Date(newVal).setFullYear(year + 1);
                    let dateUTC = new Date(date).toUTCString();
                    this.mentor.endDateMentoring = dateUTC;
                } else {
                    this.mentor.endDateMentoring = new Date();
                }
            }
        },
        mounted() {
            this.id = this.$route.params.id;
            if (this.id) {
                this.loadData();
            }
        }
    }
</script>

<style scoped>
    input[readonly] {
        background-color: whitesmoke;
    }

    textarea[readonly] {
        background-color: whitesmoke;
    }
</style>
